import { useEffect, useState } from "react";
import { ExternalLink, Tag, X } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

interface Ad {
  id: string;
  name: string;
  description: string;
  offer: string;
  city: string;
  url: string;
}

const HIDE_KEY = "nutricart.ads.hidden";
const HIDE_DAYS = 7;

/** Offers the person closed, with when to show them again (kept on this device only). */
function hiddenIds(): string[] {
  try {
    const map = JSON.parse(localStorage.getItem(HIDE_KEY) ?? "{}") as Record<string, number>;
    return Object.entries(map)
      .filter(([, until]) => until > Date.now())
      .map(([id]) => id);
  } catch {
    return [];
  }
}
function hide(id: string) {
  try {
    const map = JSON.parse(localStorage.getItem(HIDE_KEY) ?? "{}") as Record<string, number>;
    map[id] = Date.now() + HIDE_DAYS * 86_400_000;
    localStorage.setItem(HIDE_KEY, JSON.stringify(map));
  } catch {
    /* only a convenience */
  }
}

/** Count a click without waiting for it, so the partner's page opens straight away. */
export function countAdClick(id: string, placement: "home" | "list" | "places" | "fridge") {
  fetch(`/api/ads/${id}/click`, {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-Nutricart": "1" },
    body: JSON.stringify({ placement }),
  }).catch(() => {});
}

/**
 * One partner offer, clearly marked as an ad: never a pop-up, at most one per screen, and the person can
 * close it (it stays away for a week). Views and clicks are counted anonymously for the partner's report.
 */
export function AdCard({ placement }: { placement: "home" | "list" | "fridge" }) {
  const { t } = useI18n();
  const [ad, setAd] = useState<Ad | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<{ ad: Ad | null }>(`/api/ads?placement=${placement}&hide=${hiddenIds().join(",")}`)
      .then((r) => alive && setAd(r.ad))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [placement]);

  if (!ad) return null;
  return (
    <aside className="card ad-card" aria-label={t("ads.label")}>
      <div className="ad-head">
        <span className="ad-label">{t("ads.label")}</span>
        <button
          type="button"
          className="icon-btn"
          aria-label={t("ads.close")}
          onClick={() => {
            hide(ad.id);
            setAd(null);
          }}
        >
          <X size={18} />
        </button>
      </div>
      <strong className="ad-name">
        {ad.name}
        {ad.city && <span className="small muted"> · {ad.city}</span>}
      </strong>
      {ad.offer && (
        <span className="ad-offer">
          <Tag size={16} aria-hidden="true" /> {ad.offer}
        </span>
      )}
      {ad.description && <span className="small muted">{ad.description}</span>}
      {ad.url && (
        <a className="btn small secondary ad-cta" href={ad.url} target="_blank" rel="noopener sponsored" onClick={() => countAdClick(ad.id, placement)}>
          <ExternalLink size={16} aria-hidden="true" /> {t("ads.open")}
        </a>
      )}
    </aside>
  );
}
